/**
 * Speech-to-text engine selection for the SDK's browser mic path.
 *
 * Historically `sttFromMic` (audioUtils.ts) was Azure-only: an Azure STS
 * token from the unauthenticated `/talk/get-speech-token` mint, then the
 * Azure Speech SDK recognizer. Camlin Speech ASR is now a first-class
 * alternative (Keith, 6 Aug 2026): recognition goes formally through the
 * Camlin Speech STT API (`POST /api/v1/asr/recognize`) with a short-lived,
 * tenant-bound `cat_…` token minted server-side — never a raw API key in
 * the browser — which is also how per-tenant lexicon biasing (word
 * injection) reaches the mic path, something the Azure path never had.
 *
 * Selection is a module-level registry rather than a prop thread: the mic
 * capture entry point (`sttFromMic`) sits under `useLiveListenControls`
 * behind many host surfaces, and every one of them should follow the site
 * config without new plumbing. A host (e.g. the Companion widget reading
 * its site config's `asr` block) calls `configureSttEngine(...)` once at
 * mount; unconfigured means Azure, exactly as before.
 */

export interface SttEngineConfig {
  /** 'azure' (default, unchanged behaviour) or 'camlin'. */
  engine: 'azure' | 'camlin';
  /**
   * Camlin engine: URL returning `{token: "cat_…", expires_at}` JSON on
   * GET — a same-origin server route that holds the real API key and
   * calls the STT API's `POST /api/v1/asr/token`. Required for 'camlin'.
   */
  tokenUrl?: string;
  /**
   * Camlin engine: the recognize endpoint (WAV in, transcript JSON out).
   * Defaults to `/api/v1/asr/recognize` resolved against the page origin,
   * which is correct when the page is served behind the Camlin Speech ALB.
   */
  recognizeUrl?: string;
  /** BCP-47-ish language tag forwarded as x-camlin-asr-language. */
  language?: string;
}

const DEFAULT_CONFIG: SttEngineConfig = { engine: 'azure' };

let currentConfig: SttEngineConfig = DEFAULT_CONFIG;

export function configureSttEngine(config: SttEngineConfig | null | undefined): void {
  if (!config || config.engine !== 'camlin') {
    currentConfig = DEFAULT_CONFIG;
    return;
  }
  if (!config.tokenUrl) {
    console.warn('[sttEngine] camlin engine requested without tokenUrl — staying on azure');
    currentConfig = DEFAULT_CONFIG;
    return;
  }
  currentConfig = {
    engine: 'camlin',
    tokenUrl: config.tokenUrl,
    recognizeUrl: config.recognizeUrl || '/api/v1/asr/recognize',
    language: config.language
  };
  console.log('[sttEngine] Camlin Speech ASR engine selected', {
    tokenUrl: currentConfig.tokenUrl,
    recognizeUrl: currentConfig.recognizeUrl
  });
}

export function getSttEngineConfig(): SttEngineConfig {
  return currentConfig;
}
